import type { ReactElement } from 'react';
import { forwardRef, useCallback } from 'react';

// --- Lib ---
import { parseShortcutKeys } from 'containers/Tiptap/lib/tiptap-utils';

// --- Hooks ---
import { useTiptapEditor } from 'containers/Tiptap/hooks/use-tiptap-editor';

// --- Tiptap UI ---
import type { UseDuplicateConfig } from 'containers/Tiptap/components/tiptap-ui/duplicate-button';
import { DUPLICATE_SHORTCUT_KEY, useDuplicate } from 'containers/Tiptap/components/tiptap-ui/duplicate-button';

// --- UI Primitives ---
import type { ButtonProps } from 'containers/Tiptap/components/tiptap-ui-primitive/button';
import { Button } from 'containers/Tiptap/components/tiptap-ui-primitive/button';
import { Badge } from 'containers/Tiptap/components/tiptap-ui-primitive/badge';

export interface DuplicateButtonProps extends Omit<ButtonProps, 'type'>, UseDuplicateConfig {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Optional show shortcut keys in the button.
   * @default false
   */
  showShortcut?: boolean;
}

export function DuplicateShortcutBadge({ shortcutKeys = DUPLICATE_SHORTCUT_KEY }: { shortcutKeys?: string }): ReactElement {
  // @ts-ignore
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for duplicating a node in a Tiptap editor.
 *
 * For custom button implementations, use the `useDuplicate` hook instead.
 */
export const DuplicateButton = forwardRef<HTMLButtonElement, DuplicateButtonProps>(
  (
    {
      editor: providedEditor,
      text,
      hideWhenUnavailable = false,
      onDuplicated,
      showShortcut = false,
      onClick,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const { isVisible, handleDuplicate, label, shortcutKeys, Icon } = useDuplicate({
      editor,
      hideWhenUnavailable,
      onDuplicated,
    });

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        handleDuplicate();
      },
      [handleDuplicate, onClick]
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Button
        type="button"
        data-style="ghost"
        role="button"
        tabIndex={-1}
        aria-label={label}
        tooltip="Duplicate"
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <>
            <Icon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
            {showShortcut && <DuplicateShortcutBadge shortcutKeys={shortcutKeys} />}
          </>
        )}
      </Button>
    );
  }
);

DuplicateButton.displayName = 'DuplicateButton';
